import { useState, useEffect, useCallback } from 'react';
import { propertyAPI, Property, CreatePropertyData, UpdatePropertyData } from '../api/properties';
import { useAuth } from '../contexts/AuthContext';

export const useProperties = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProperties = async () => {
      // ⛔ SÉCURITÉ : Ne pas charger les données si pas d'utilisateur ou si admin sans agence
      if (!user) {
        console.log('⛔ Pas d\'utilisateur connecté');
        setIsLoading(false);
        return;
      }

      // ⛔ Les admins système (sans agency_id) ne voient pas de propriétés
      if (!user.agency_id) {
        console.log('⛔ Admin système : pas d\'agence associée, pas de propriétés');
        setProperties([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        console.log('🔒 Fetching properties for agency_id:', user.agency_id);
        // ✅ FILTRE PAR AGENCE : Ne récupérer QUE les biens de l'agence de l'utilisateur
        const data = await propertyAPI.getAll({ agency_id: user.agency_id });
        console.log('✅ Properties loaded for agency', user.agency_id, ':', data.length, 'biens');
        console.log('📸 Sample property photos:', data[0]?.photos);
        setProperties(data);
      } catch (error) {
        console.error('Failed to fetch properties:', error);
        setError('Erreur lors du chargement des biens');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperties();
  }, [user]);

  const createProperty = useCallback(async (propertyData: CreatePropertyData) => {
    if (!user?.agency_id || !user?.id) {
      console.error('⛔ Impossible de créer un bien : utilisateur non authentifié');
      setError('Utilisateur non authentifié');
      return false;
    }

    setError(null);
    try {
      console.log('Données avant envoi à l\'API:', propertyData);
      const createdProperty = await propertyAPI.create(propertyData, user.agency_id, user.id);
      setProperties(prev => [createdProperty, ...prev]);
      return true;
    } catch (error) {
      console.error('Failed to create property:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        propertyData
      });
      setError(error instanceof Error ? error.message : 'Erreur lors de la création');
      return false;
    }
  }, [user]);

  const updateProperty = useCallback(async (propertyId: number, propertyData: UpdatePropertyData) => {
    if (!user?.agency_id) {
      console.error('⛔ Impossible de mettre à jour un bien : utilisateur non authentifié');
      setError('Utilisateur non authentifié');
      return false;
    }

    setError(null);
    try {
      const updatedProperty = await propertyAPI.update(propertyId, propertyData, user.agency_id);
      setProperties(prev => prev.map(property =>
        property.id === propertyId ? updatedProperty : property
      ));
      return true;
    } catch (error) {
      console.error('Failed to update property:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la mise à jour');
      return false;
    }
  }, [user]);

  const getPropertyById = useCallback((id: string): Property | undefined => {
    console.log('🔍 getPropertyById appelé avec ID:', id, 'Type:', typeof id);
    console.log('🔍 IDs disponibles:', properties.map(p => ({ id: p.id, type: typeof p.id })));
    
    // Essayer plusieurs méthodes de comparaison
    let foundProperty = properties.find(property => property.id.toString() === id.toString());
    
    if (!foundProperty) {
      // Essayer avec conversion en nombre si possible
      const numericId = parseInt(id);
      if (!isNaN(numericId)) {
        foundProperty = properties.find(property => parseInt(property.id.toString()) === numericId);
      }
    }
    
    console.log('🏠 Propriété trouvée:', foundProperty ? `ID: ${foundProperty.id}, Type: ${foundProperty.type}` : 'AUCUNE');
    return foundProperty;
  }, [properties]);

  const archiveProperty = useCallback(async (propertyId: string) => {
    setError(null);
    try {
      await propertyAPI.archive(parseInt(propertyId));
      setProperties(prev => prev.map(property =>
        property.id.toString() === propertyId
          ? { ...property, status: 'archived' }
          : property
      ));
      return true;
    } catch (error) {
      console.error('Failed to archive property:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'archivage');
      return false;
    }
  }, []);

  const deleteProperty = useCallback(async (propertyId: string) => {
    setError(null);
    try {
      await propertyAPI.delete(parseInt(propertyId));
      setProperties(prev => prev.filter(property => property.id.toString() !== propertyId));
      return true;
    } catch (error) {
      console.error('Failed to delete property:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  const getPropertiesByStatus = useCallback((status: string) => {
    return properties.filter(property => property.status === status);
  }, [properties]);

  return {
    properties,
    isLoading,
    error,
    createProperty,
    updateProperty,
    getPropertyById,
    archiveProperty,
    deleteProperty,
    getPropertiesByStatus
  };
};
